import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { useNotifications } from '../hooks/useNotifications';
import NotificationItem from './NotificationItem';
import { sortNotifications } from '../utils/notificationHelpers';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/features/auth/hooks/useAuth';
import useDeviceType from '@/hooks/useDeviceType';
import { Button } from '@/components/ui';

/**
 * NotificationTray Component
 * Bell icon with unread count badge and dropdown.
 *
 * Redesigned for flat Linear-style aesthetic:
 *  - Flat divide-y list (no per-item card wrappers)
 *  - Footer: plain "View all →" text link (not a full-width button)
 *  - Mobile close: XMarkIcon (not close character)
 */
const NotificationTray = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const deviceType = useDeviceType();

  // Call hooks at top level (needed for debug info)
  const { schoolId } = useSchool();
  const { user } = useAuth();

  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    dismiss,
  } = useNotifications({
    limit: 50, // Over-fetch to compensate for client-side dismissed/expired filtering
    includeRead: false, // Only show unread in dropdown
  });

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    // Skip on mobile - mobile modal has its own close button
    if (deviceType.isMobile) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, deviceType.isMobile]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Sort notifications by priority and date
  const sortedNotifications = sortNotifications(notifications);
  const displayNotifications = sortedNotifications.slice(0, 10);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Bell Icon Button */}
        <Button
          className="btn-ghost btn-circle relative"
          onClick={toggleDropdown}
          aria-label="Notifications"
        >
          {unreadCount > 0 ? (
            <BellIconSolid className="w-6 h-6 text-primary" />
          ) : (
            <BellIcon className="w-6 h-6" />
          )}

          {/* Unread Count Badge */}
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-error rounded-full">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>

        {/* Desktop Dropdown */}
        {isOpen && !deviceType.isMobile && (
          <div className="absolute right-0 mt-2 w-96 bg-base-100 rounded-lg shadow-xl border border-base-300 z-50 max-h-[600px] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-base-300">
              <h3 className="text-lg font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  className="btn-ghost btn-sm"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all read
                </Button>
              )}
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <span className="loading loading-spinner loading-md"></span>
                </div>
              ) : displayNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <BellIcon className="w-12 h-12 text-base-300 mb-3" />
                  <p className="text-base-content/70">No unread notifications</p>
                  <p className="text-sm text-base-content/50 mt-1">
                    {loading ? 'Loading...' : "You're all caught up!"}
                  </p>
                  {/* Debug info in development */}
                  {import.meta.env.DEV && (
                    <div className="mt-4 p-2 bg-base-200 rounded text-xs">
                      <div>Debug Info:</div>
                      <div>Total notifications: {notifications.length}</div>
                      <div>Unread count: {unreadCount}</div>
                      <div>School ID: {schoolId || 'Not set'}</div>
                      <div>User ID: {user?.uid || 'Not set'}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-base-200">
                  {displayNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      userId={user?.uid}
                      onMarkAsRead={markAsRead}
                      onDismiss={dismiss}
                      onNavigate={() => setIsOpen(false)}
                      compact
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer — plain text link */}
            {!loading && displayNotifications.length > 0 && (
              <div className="border-t border-base-300 px-4 py-2.5 flex justify-end">
                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className={[
                    // qc:allow tight-spacing
                    'text-sm text-primary hover:underline inline-flex items-center gap-1',
                  ].join(' ')}
                >
                  View all
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Modal - Full screen overlay */}
      {isOpen && deviceType.isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-base-100 w-screen max-w-none rounded-t-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ marginLeft: 0, marginRight: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-base-300">
              <h3 className="text-lg font-semibold">Notifications</h3>
              {/* qc:allow tight-spacing — header action pair sits tight */}
              <div className="flex gap-2 items-center">
                {unreadCount > 0 && (
                  <Button
                    className="btn-ghost btn-sm"
                    onClick={handleMarkAllAsRead}
                  >
                    Mark all read
                  </Button>
                )}
                <Button
                  className="btn-circle btn-sm btn-ghost"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close notifications"
                >
                  <XMarkIcon className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Notification List */}
            <div
              className="overflow-y-auto flex-1 [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <span className="loading loading-spinner loading-md"></span>
                </div>
              ) : displayNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <BellIcon className="w-12 h-12 text-base-300 mb-3" />
                  <p className="text-base-content/70">No unread notifications</p>
                  <p className="text-sm text-base-content/50 mt-1">
                    You're all caught up!
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-base-200">
                  {displayNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      userId={user?.uid}
                      onMarkAsRead={markAsRead}
                      onDismiss={dismiss}
                      onNavigate={() => setIsOpen(false)}
                      compact
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer — plain text link */}
            {!loading && displayNotifications.length > 0 && (
              <div className="border-t border-base-300 px-4 py-2.5 flex justify-end">
                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className={[
                    // qc:allow tight-spacing
                    'text-sm text-primary hover:underline inline-flex items-center gap-1',
                  ].join(' ')}
                >
                  View all
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationTray;
